import {
  ActionIcon,
  Autocomplete,
  Button,
  Input,
  NumberInput,
  Select,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconBarcode,
  IconCategory,
  IconFileText,
  IconPackage,
  IconRuler,
  IconScale,
  IconUpload,
  IconUser,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import { notification } from "antd";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  allSteelSubCategories,
  categories,
  materials,
  steelMaterials,
  type Item,
} from "./ItemEditor/data";
import {
  mapServerItem,
  REAL_CATEGORY_IDS,
  type ServerItemRow,
} from "./ItemEditor/itemMapping";
import {
  calculateSteelWeight,
  formatWeight,
  LENGTH_DECIMALS,
  roundLength,
  roundUnitWeight,
  steelWeight,
  UNIT_WEIGHT_DECIMALS,
} from "./ItemEditor/steelWeight";

type ProductForm = {
  name: string;
  description: string;
  itemId: string | null;
  defaultItemId: string | null;
  partnerId: string | null;
  unitWeight: number | string;
  unit: string;
  material: string;
  length: number | string;
  inventoryLowerLimit: number | string;
  inventoryLowerLimitContactId: string | null;
  image: string | null;
};

function toNumberOrNull(value: number | string) {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function encodeItemValue(item: Item) {
  return `${item.isPreset ? "default" : "company"}:${item.id}`;
}

function matchesCategory(item: Item, category: string) {
  if (category === "uncategorized")
    return !REAL_CATEGORY_IDS.has(item.categoryId);
  return item.categoryId === category;
}

function steelUnitWeight(item: Item | null) {
  if (!item || item.categoryId !== "steel" || !item.steelType || !item.spec) {
    return null;
  }
  const result = calculateSteelWeight(item.steelType, item.spec);
  return result ? result.unitWeightKgM : null;
}

function AddProductListPage() {
  const { t } = useTranslation();
  const [currentStage, setCurrentStage] = useState(0);
  const [items, setItems] = useState<Item[]>([]);
  const [category, setCategory] = useState<string | null>(null);
  const [steelType, setSteelType] = useState<string | null>(null);
  const [selectedItemValue, setSelectedItemValue] = useState<string | null>(
    null,
  );
  const [employeeData, setEmployeeData] = useState([]);
  const [partnerData, setPartnerData] = useState([]);
  const [existingProducts, setExistingProducts] = useState<
    { name: string; partnerId: string | null }[]
  >([]);
  const [productData, setProductData] = useState<ProductForm>({
    name: "",
    description: "",
    itemId: null,
    defaultItemId: null,
    partnerId: null,
    unitWeight: "",
    unit: "",
    material: "",
    length: "",
    inventoryLowerLimit: "",
    inventoryLowerLimitContactId: null,
    image: null,
  });

  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const handleDataChange = (name, value) => {
    setProductData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const [imageUploading, setImageUploading] = useState(false);

  // 先上傳到 storage，欄位只存 blobUrl（同員工大頭貼），不存 base64
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || imageUploading) return;
    setImageUploading(true);
    try {
      const { blobSasUrl, blobUrl } = await orpcClient.storage.createBlobSasUrl(
        {
          fileName: file.name,
          contentType: file.type,
        },
      );
      const res = await fetch(blobSasUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type, "x-ms-blob-type": "BlockBlob" },
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      handleDataChange("image", blobUrl);
    } catch (error) {
      console.error("upload error", error);
      api.error({
        message: `Notification`,
        description: <div>{t("warehouse.imageUploadError")}</div>,
        placement: "topLeft",
      });
    } finally {
      setImageUploading(false);
    }
  };

  const removeImage = () => handleDataChange("image", null);

  const itemMap = useMemo(() => {
    const map = new Map<string, Item>();
    for (const item of items) {
      map.set(encodeItemValue(item), item);
    }
    return map;
  }, [items]);

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((c) => c.id !== "all")
        .map((c) => ({ value: c.id, label: c.name })),
    [],
  );

  const steelTypeOptions = useMemo(() => {
    if (category !== "steel") return [];
    const present = new Set(
      items
        .filter((item) => item.categoryId === "steel" && item.steelType)
        .map((item) => item.steelType as string),
    );
    return allSteelSubCategories
      .filter((sub) => present.has(sub.id))
      .map((sub) => ({ value: sub.id, label: `${sub.id} - ${sub.name}` }));
  }, [items, category]);

  const itemOptions = useMemo(() => {
    if (!category) return [];
    let list: Item[];
    if (category === "steel") {
      if (!steelType) return [];
      list = items.filter(
        (item) => item.categoryId === "steel" && item.steelType === steelType,
      );
    } else {
      list = items.filter((item) => matchesCategory(item, category));
    }
    return list.map((item) => ({
      value: encodeItemValue(item),
      label: item.name,
    }));
  }, [items, category, steelType]);

  const selectedItem = selectedItemValue
    ? (itemMap.get(selectedItemValue) ?? null)
    : null;

  const isSteel = selectedItem?.categoryId === "steel";
  const isScrew = selectedItem?.categoryId === "screw";
  const isOther = !!selectedItem && !isSteel && !isScrew;

  const showItemSelect = !!category && (category !== "steel" || !!steelType);

  const {
    name,
    description,
    partnerId,
    unitWeight,
    unit,
    material,
    length,
    inventoryLowerLimit,
  } = productData;

  const weight = useMemo(() => {
    if (!isSteel) return "";
    return formatWeight(steelWeight(unitWeight, length));
  }, [isSteel, unitWeight, length]);

  const materialOptions = useMemo(
    () => (isSteel ? steelMaterials : materials),
    [isSteel],
  );

  const isDuplicateName = useMemo(() => {
    const trimmedName = name.trim();
    if (!trimmedName || !partnerId) return false;
    return existingProducts.some(
      (p) => p.partnerId === partnerId && p.name?.trim() === trimmedName,
    );
  }, [existingProducts, partnerId, name]);

  const resetItemSelection = () => {
    setSelectedItemValue(null);
    setProductData((prev) => ({
      ...prev,
      itemId: null,
      defaultItemId: null,
      name: "",
      description: "",
      unitWeight: "",
      unit: "",
      length: "",
    }));
  };

  const handleCategoryChange = (value: string | null) => {
    setCategory(value);
    setSteelType(null);
    resetItemSelection();
  };

  const handleSteelTypeChange = (value: string | null) => {
    setSteelType(value);
    resetItemSelection();
  };

  const handleSelectItem = (value: string | null) => {
    setSelectedItemValue(value);
    const item = value ? (itemMap.get(value) ?? null) : null;
    const presetWeight = steelUnitWeight(item);
    setProductData((prev) => ({
      ...prev,
      itemId: item && !item.isPreset ? item.id : null,
      defaultItemId: item && item.isPreset ? item.id : null,
      name: item?.name ?? "",
      description: item?.description ?? "",
      unitWeight: roundUnitWeight(presetWeight) ?? "",
      unit: "",
      length: "",
    }));
  };

  const handleChangePartnerData = (value, data, dataType) => {
    let match = value.match(/ID:\s*([a-zA-Z0-9-]+)/);
    let idNumber = match ? match[1] : null;

    let selectedData = data.filter((e) => e?.id == idNumber)[0];
    handleDataChange(dataType, selectedData?.id);
  };

  const handleSubmit = async () => {
    if (!selectedItem) {
      api.error({
        message: `Notification`,
        description: <div>請先選擇品項</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
    if (
      isSteel &&
      (toNumberOrNull(unitWeight) === null || toNumberOrNull(length) === null)
    ) {
      api.error({
        message: `Notification`,
        description: <div>請輸入單位重與長度</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
    if (isScrew && (!unit?.trim() || toNumberOrNull(length) === null)) {
      api.error({
        message: `Notification`,
        description: <div>請輸入單位與長度</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
    if (!partnerId) {
      api.error({
        message: `Notification`,
        description: <div>請選擇合作方</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
    if (!name?.trim()) {
      api.error({
        message: `Notification`,
        description: <div>請輸入商品名稱</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
    if (isDuplicateName) {
      api.error({
        message: `Notification`,
        description: <div>此合作方已有相同商品名稱，請使用其他名稱</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }

    try {
      const payload = {
        itemId: productData.itemId ?? undefined,
        defaultItemId: productData.defaultItemId ?? undefined,
        partnerId,
        name: name.trim(),
        description: description || null,
        length: isSteel || isScrew ? roundLength(length) : null,
        unitWeight: isSteel ? roundUnitWeight(unitWeight) : null,
        unit: isScrew || isOther ? (unit?.trim() ? unit.trim() : null) : null,
        material: material?.trim() ? material.trim() : null,
        inventoryLowerLimit: toNumberOrNull(inventoryLowerLimit),
        inventoryLowerLimitContactId:
          productData.inventoryLowerLimitContactId || null,
        image: productData.image || null,
      };

      const result = await single(orpcClient.product.create([payload]));

      if (result) {
        setCurrentStage(1);
      } else {
        api.error({
          message: `Notification`,
          description: <div>建立商品失敗，請檢查資料後重試</div>,
          placement: "topLeft",
          duration: 0,
        });
      }
    } catch (error) {
      api.error({
        message: `Notification`,
        description: (
          <div>
            {(error as any)?.message || "建立商品時發生錯誤，請稍後重試"}
          </div>
        ),
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyItems, defaultItems] = await Promise.all([
          orpcClient.item.list({}),
          orpcClient.defaultItem.list({}),
        ]);
        setItems([
          ...companyItems.map((row: ServerItemRow) =>
            mapServerItem(row, false),
          ),
          ...defaultItems.map((row: ServerItemRow) => mapServerItem(row, true)),
        ]);
      } catch (error) {
        console.error("Error fetching items data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.partner.list({});
        setPartnerData(result);
      } catch (error) {
        console.error("Error fetching partner data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.employee.list({});
        setEmployeeData(result);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.product.list({});
        setExistingProducts(result);
      } catch (error) {
        console.error("Error fetching product data:", error);
      }
    };

    fetchData();
  }, []);

  const fieldWrapper = (
    label: React.ReactNode,
    Icon: React.ComponentType<{ size?: number | string }> | undefined,
    children: React.ReactNode,
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {children}
    </div>
  );

  return (
    <>
      {contextHolder}
      {currentStage === 0 ? (
        <div className="min-h-screen p-8">
          <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
            <div>
              <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
                {t("warehouse.createProduct") || "建立商品"}
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {t("warehouse.productManagement") || "商品管理"}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
                <IconCategory size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("warehouse.selectItem") || "品項選擇"}
                </h3>
              </div>
              <div className="space-y-4 p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {fieldWrapper(
                    `${t("warehouse.itemCategory") || "品項分類"}＊`,
                    IconCategory,
                    <Select
                      value={category}
                      data={categoryOptions}
                      onChange={handleCategoryChange}
                      placeholder={
                        t("warehouse.selectCategory") || "選擇品項分類"
                      }
                      leftSection={<IconCategory size={16} />}
                      searchable
                      clearable
                      nothingFoundMessage="找不到分類"
                    />,
                  )}

                  {category === "steel" &&
                    fieldWrapper(
                      `${t("warehouse.steelItemType") || "型鋼類型"}＊`,
                      IconCategory,
                      <Select
                        value={steelType}
                        data={steelTypeOptions}
                        onChange={handleSteelTypeChange}
                        placeholder={
                          t("warehouse.selectSteelType") || "選擇型鋼類型"
                        }
                        leftSection={<IconCategory size={16} />}
                        searchable
                        clearable
                        nothingFoundMessage="找不到型鋼類型"
                      />,
                    )}
                </div>

                {showItemSelect &&
                  fieldWrapper(
                    `${t("warehouse.selectItem") || "選擇品項"}＊`,
                    IconPackage,
                    <Select
                      value={selectedItemValue}
                      data={itemOptions}
                      onChange={handleSelectItem}
                      placeholder={t("warehouse.selectItem") || "選擇品項"}
                      leftSection={<IconPackage size={16} />}
                      searchable
                      clearable
                      nothingFoundMessage="此分類下尚無品項"
                      maxDropdownHeight={320}
                    />,
                  )}
              </div>
            </div>

            {selectedItem && (
              <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
                  <IconFileText size={16} className="text-blue-600" />
                  <h3 className="text-sm font-medium text-gray-700">
                    {t("companyInfo.basicInfo") || "基本資訊"}
                  </h3>
                </div>
                <div className="space-y-4 p-6">
                  {isSteel &&
                    selectedItem.spec &&
                    fieldWrapper(
                      t("warehouse.sectionDimension") || "斷面規格",
                      IconRuler,
                      <Input value={selectedItem.spec} disabled />,
                    )}

                  <div className="grid grid-cols-1 gap-4">
                    {fieldWrapper(
                      `${t("warehouse.productName") || "商品名稱"}＊`,
                      IconPackage,
                      <div>
                        <Input
                          value={name}
                          onChange={({ target: { value } }) =>
                            handleDataChange("name", value)
                          }
                          leftSection={<IconPackage size={16} />}
                          error={isDuplicateName}
                        />
                        {isDuplicateName && (
                          <div
                            style={{ color: "#FA5252", fontSize: "0.75rem" }}
                            className="pt-1"
                          >
                            {t("warehouse.productNameDuplicate") ||
                              "此合作方已有相同商品名稱，請使用其他名稱"}
                          </div>
                        )}
                      </div>,
                    )}

                    {/* {fieldWrapper(
                      `${t("warehouse.productCode") || "商品編號"}＊`,
                      IconBarcode,
                      <Input
                        value={code}
                        onChange={({ target: { value } }) =>
                          handleDataChange("code", value)
                        }
                        leftSection={<IconBarcode size={16} />}
                      />,
                    )} */}
                  </div>

                  {isSteel && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      {fieldWrapper(
                        `${t("warehouse.unitWeight") || "單位重(kg/m)"}＊`,
                        IconScale,
                        <NumberInput
                          value={unitWeight}
                          onChange={(value) =>
                            handleDataChange("unitWeight", value)
                          }
                          leftSection={<IconScale size={16} />}
                          allowNegative={false}
                          decimalScale={UNIT_WEIGHT_DECIMALS}
                          min={0}
                        />,
                      )}
                      {fieldWrapper(
                        `${t("warehouse.length") || "長度"}＊`,
                        IconRuler,
                        <NumberInput
                          value={length}
                          onChange={(value) =>
                            handleDataChange("length", value)
                          }
                          leftSection={<IconRuler size={16} />}
                          decimalScale={LENGTH_DECIMALS}
                          allowNegative={false}
                          min={0}
                        />,
                      )}
                      {fieldWrapper(
                        t("warehouse.weight") || "重量",
                        IconScale,
                        <Input
                          value={weight}
                          disabled
                          placeholder={
                            t("warehouse.autoCalculated") || "自動計算"
                          }
                          leftSection={<IconScale size={16} />}
                        />,
                      )}
                    </div>
                  )}

                  {isScrew && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {fieldWrapper(
                        `${t("warehouse.unit") || "單位"}＊`,
                        IconScale,
                        <Input
                          value={unit}
                          onChange={({ target: { value } }) =>
                            handleDataChange("unit", value)
                          }
                          leftSection={<IconScale size={16} />}
                        />,
                      )}
                      {fieldWrapper(
                        `${t("warehouse.length") || "長度"}＊`,
                        IconRuler,
                        <NumberInput
                          value={length}
                          onChange={(value) =>
                            handleDataChange("length", value)
                          }
                          leftSection={<IconRuler size={16} />}
                          decimalScale={LENGTH_DECIMALS}
                          allowNegative={false}
                          min={0}
                        />,
                      )}
                    </div>
                  )}

                  {isOther &&
                    fieldWrapper(
                      t("warehouse.unit") || "單位",
                      IconScale,
                      <Input
                        value={unit}
                        onChange={({ target: { value } }) =>
                          handleDataChange("unit", value)
                        }
                        leftSection={<IconScale size={16} />}
                      />,
                    )}

                  {fieldWrapper(
                    t("warehouse.material") || "材質",
                    IconCategory,
                    <Autocomplete
                      value={material}
                      data={materialOptions}
                      onChange={(value) => handleDataChange("material", value)}
                      leftSection={<IconCategory size={16} />}
                    />,
                  )}
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
                <IconUsers size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("warehouse.partner") || "合作方"}
                </h3>
              </div>
              <div className="p-6">
                {fieldWrapper(
                  `${t("warehouse.partner") || "合作方"}＊`,
                  IconUsers,
                  <Select
                    leftSection={<IconUsers size={16} />}
                    value={partnerId}
                    data={partnerData?.map((p) => ({
                      value: p?.id,
                      label: p?.name,
                    }))}
                    onChange={(value) => handleDataChange("partnerId", value)}
                    searchable
                    clearable
                    nothingFoundMessage="找不到合作方"
                  />,
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
                <IconFileText size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("warehouse.productDescription") || "商品描述"}
                </h3>
              </div>
              <div className="space-y-4 p-6">
                {fieldWrapper(
                  t("warehouse.productDescription") || "商品描述",
                  IconFileText,
                  <Input
                    leftSection={<IconFileText size={16} />}
                    value={description}
                    onChange={(e) =>
                      handleDataChange("description", e.target.value)
                    }
                  />,
                )}

                {fieldWrapper(
                  t("warehouse.productImage") || "商品圖片",
                  IconUpload,
                  productData.image ? (
                    <div className="relative h-40 w-full max-w-[420px] overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                      <img
                        src={productData.image}
                        alt="預覽"
                        className="h-full w-full object-contain"
                      />
                      <ActionIcon
                        color="red"
                        variant="filled"
                        className="!absolute right-2 top-2"
                        onClick={removeImage}
                      >
                        <IconX size={16} />
                      </ActionIcon>
                    </div>
                  ) : (
                    <label className="flex h-32 w-full max-w-[420px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 transition-colors hover:bg-gray-50">
                      <div className="flex flex-col items-center justify-center pb-6 pt-5">
                        <IconUpload size={32} className="mb-2 text-gray-500" />
                        <p className="text-sm text-gray-500">
                          {imageUploading
                            ? t("warehouse.imageUploading")
                            : t("warehouse.uploadImage") || "點擊上傳圖片"}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          PNG, JPG, GIF (最大 5MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        disabled={imageUploading}
                        onChange={handleImageChange}
                      />
                    </label>
                  ),
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
                <IconAlertCircle size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("warehouse.inventoryLowerLimit") || "設定庫存下限"}
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
                {fieldWrapper(
                  t("warehouse.inventoryLowerLimit") || "設定庫存下限",
                  IconAlertCircle,
                  <NumberInput
                    leftSection={<IconAlertCircle size={16} />}
                    value={inventoryLowerLimit}
                    onChange={(value) =>
                      handleDataChange("inventoryLowerLimit", value)
                    }
                    allowDecimal={false}
                    allowNegative={false}
                    min={0}
                  />,
                )}
                {fieldWrapper(
                  t("warehouse.inventoryLowerLimitContact") || "庫存下限通知人",
                  IconUser,
                  <Autocomplete
                    leftSection={<IconUser size={16} />}
                    data={employeeData?.map((t) => t?.name + ` (ID: ${t?.id})`)}
                    onChange={(e) =>
                      handleChangePartnerData(
                        e,
                        employeeData,
                        "inventoryLowerLimitContactId",
                      )
                    }
                  />,
                )}
              </div>
            </div>

            <Button
              variant="filled"
              color="gray"
              style={{ width: "100%" }}
              radius="md"
              disabled={imageUploading}
              onClick={() => handleSubmit()}
            >
              {t("quotation.create") || "建立"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="min-h-screen flex h-full items-center justify-center p-14 text-center">
          <div className="w-[487px] rounded-xl bg-white p-8">
            <div style={{ color: "#6F6F6F" }}>
              {t("warehouse.productCreateSuccess") || "建立倉儲成功！"}
            </div>
            <Button
              variant="filled"
              color="gray"
              style={{ width: "100%", margin: "1rem 0" }}
              radius="md"
              onClick={() => navigate("/hrMainPage/product")}
            >
              {t("signup.confirm") || "確定"}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
export default AddProductListPage;
